/**
 * Real self-fetching Timesheets hook (#23312) — mirrors `use_employees.ts` /
 * `use_data_centre.ts`'s established pattern: the real bearer client
 * (`getTrpcClient()`, `lib/trpc.ts`) + the shared `useOAuthEntityContext()`
 * helper, DI-testable fetch/mutation seams, an explicit state machine
 * (`loading`/`no_entity`/`entitlement_required`/`error`/`ready`), no mock
 * fallback.
 *
 * Both procedures wired here are gated by `requirePayrollAddon`
 * (`payrollEntityProcedure` / `payrollWriteProcedure` in
 * `apps/api/src/trpc/routers/payroll.ts`), so a FORBIDDEN carrying the
 * add-on-required marker is classified as `entitlement_required` via
 * `isPayrollAddonRequiredError` (same stance as `use_pay_runs.ts` — plan-gate
 * classification here would silently mask a real auth bug behind an "upgrade"
 * prompt).
 *
 * The `create` write path is `undefined` (hides the affordance entirely)
 * unless the OAuth grant is write-capable per `isWriteCapableGrant`
 * (`use_pay_runs.ts`) — the same predicate used everywhere else in this app,
 * so a read-only WagePro token never sees a button that would just 403.
 *
 * Hours are transported as INTEGER SCALED × 10,000 per `docs/money-rules.md`
 * — never a float on the wire. `hoursDisplayToScaled` / `hoursScaledToDisplay`
 * are pure exported helpers so the boundary conversion is unit-testable.
 */
import * as React from 'react';
import {
  getTrpcClient,
  isForbiddenTrpcError,
  isPayrollAddonRequiredError,
} from '../lib/trpc.js';
import {
  useOAuthEntityContext,
  type OAuthEntityContext,
} from '../lib/auth/oauth_entity_context.js';
import { useAuth } from '../lib/auth/auth_context.js';
import { isWriteCapableGrant } from './use_pay_runs.js';

/** Input every `payroll.listTimeEntries` / `payroll.createTimeEntry` call needs to identify the entity. */
export interface TimeEntriesFetchInput {
  firm_id: string;
  entity_id: string;
}

/**
 * A single time-entry row as returned by `payroll.listTimeEntries`. `hours`
 * is the raw integer × 10,000 scaled units — the UI must call
 * {@link hoursScaledToDisplay} before rendering.
 */
export interface RealTimeEntryRecord {
  id: string;
  entity_id: string;
  employee_id: string;
  work_date: string;
  hours: number;
  description: string | null;
  created_at: string;
  updated_at: string;
}

/** Scaling constant for the `hours` column (docs/money-rules.md § Quantities). */
export const HOURS_SCALE = 10_000;

/** Hard client-side cap: 168 hours = 7 * 24, i.e. more hours than exist in a week. */
const MAX_HOURS_DISPLAY = 168;

/**
 * Convert a decimal-hours user input (e.g. "7.5") to the integer scaled
 * units the server expects. Returns `null` for empty, non-numeric, ≤0, or
 * >168 hours — the caller is expected to render the corresponding error
 * copy. Pure and exported so it is unit-testable without React.
 */
export function hoursDisplayToScaled(input: string): number | null {
  const trimmed = input.trim();
  if (trimmed.length === 0) return null;
  // Reject anything that isn't a decimal — `Number()` would happily accept
  // "1e6", hex literals, whitespace, etc.
  if (!/^\d+(\.\d+)?$/.test(trimmed)) return null;
  const parsed = Number.parseFloat(trimmed);
  if (!Number.isFinite(parsed)) return null;
  if (parsed <= 0) return null;
  if (parsed > MAX_HOURS_DISPLAY) return null;
  const scaled = Math.round(parsed * HOURS_SCALE);
  if (!Number.isSafeInteger(scaled)) return null;
  return scaled;
}

/**
 * Inverse of {@link hoursDisplayToScaled} — used both to render the list
 * cells and to pre-fill the form when re-opening it after a validation
 * failure. Trims trailing zeros so `10000` reads as `"1"`, not `"1.0000"`.
 */
export function hoursScaledToDisplay(scaled: number): string {
  const decimal = scaled / HOURS_SCALE;
  // `toFixed(4)` locks precision, then trim trailing zeros and a lone
  // trailing dot so `"7.5000"` becomes `"7.5"` and `"1.0000"` becomes `"1"`.
  const fixed = decimal.toFixed(4);
  return fixed.replace(/\.?0+$/, '');
}

/** The two failure buckets `payroll.listTimeEntries` can resolve to. */
export type TimeEntriesLoadFailure = 'entitlement_required' | 'error';

function classifyLoadFailure(err: unknown): TimeEntriesLoadFailure {
  return isPayrollAddonRequiredError(err) ? 'entitlement_required' : 'error';
}

/** The one `payroll.listTimeEntries` call {@link fetchTimeEntryRecords} needs, isolated behind an injectable seam. */
export interface TimeEntriesFetchDeps {
  listTimeEntries: (
    input: TimeEntriesFetchInput,
  ) => Promise<{ entries: RealTimeEntryRecord[] }>;
}

function defaultTimeEntriesFetchDeps(): TimeEntriesFetchDeps {
  const client = getTrpcClient();
  return {
    listTimeEntries: (input) => client.payroll.listTimeEntries.query(input),
  };
}

export type TimeEntryRecordsFetchResult =
  | { outcome: 'success'; records: RealTimeEntryRecord[] }
  | { outcome: 'failure'; failure: TimeEntriesLoadFailure };

/**
 * Resolves the entity's real time-entry list, or classifies why it couldn't.
 * Exported (and DI'd via `deps`) so the classification is unit-testable
 * against a fake client that throws a real-shaped tRPC error — mirrors
 * `use_pay_runs.ts`'s `fetchPayRunRecords`.
 *
 * `deps` is resolved INSIDE the `try` (never as a default-parameter value)
 * so a synchronous `getTrpcClient()` throw stays inside this function's
 * `try` and never escapes as an unhandled rejection past the "never throws"
 * contract.
 */
export async function fetchTimeEntryRecords(
  input: TimeEntriesFetchInput,
  deps?: TimeEntriesFetchDeps,
  isCancelled: () => boolean = () => false,
): Promise<TimeEntryRecordsFetchResult> {
  try {
    const resolvedDeps = deps ?? defaultTimeEntriesFetchDeps();
    const result = await resolvedDeps.listTimeEntries(input);
    return { outcome: 'success', records: result.entries };
  } catch (err) {
    if (!isCancelled()) {
      console.error('[wagepro-web] Failed to load time entries:', err);
    }
    return { outcome: 'failure', failure: classifyLoadFailure(err) };
  }
}

export type TimeEntriesState =
  | { status: 'loading' }
  | { status: 'no_entity' }
  | { status: 'entitlement_required' }
  | { status: 'error' }
  | { status: 'ready'; entries: RealTimeEntryRecord[] };

/**
 * Pure decision function, exported separately from the hook below so it is
 * unit-testable without rendering React. Precedence: `no_entity` always wins
 * (a caller whose grant just lost its entity binding must see `no_entity`,
 * never a leftover `error`/`entitlement_required`) — then resolved `records`
 * win over `failure` — then `entitlement_required` — then `error` — then
 * `loading`. Mirrors `resolveEmployeesState` in `use_employees.ts`.
 */
export function resolveTimeEntriesState(input: {
  entityContext: OAuthEntityContext;
  records: RealTimeEntryRecord[] | undefined;
  failure: TimeEntriesLoadFailure | undefined;
}): TimeEntriesState {
  if (input.entityContext.status === 'no_entity') {
    return { status: 'no_entity' };
  }
  if (input.records !== undefined) {
    return { status: 'ready', entries: input.records };
  }
  if (input.failure === 'entitlement_required') {
    return { status: 'entitlement_required' };
  }
  if (input.failure === 'error') {
    return { status: 'error' };
  }
  return { status: 'loading' };
}

// ── Write path ────────────────────────────────────────────────────────────

/** The four ways `payroll.createTimeEntry` can fail from the client's point of view. */
export type CreateTimeEntryFailure =
  | 'entitlement_required'
  | 'forbidden'
  | 'not_found'
  | 'error';

export interface CreateTimeEntryInput {
  employee_id: string;
  work_date: string;
  hours: number;
  description?: string;
}

export interface CreateTimeEntryDeps {
  createTimeEntry: (
    input: TimeEntriesFetchInput & CreateTimeEntryInput,
  ) => Promise<{ entry_id: string }>;
}

function defaultCreateTimeEntryDeps(): CreateTimeEntryDeps {
  const client = getTrpcClient();
  return {
    createTimeEntry: (input) => client.payroll.createTimeEntry.mutate(input),
  };
}

export type CreateTimeEntryResult =
  | { outcome: 'success'; entry_id: string }
  | { outcome: 'failure'; failure: CreateTimeEntryFailure };

function isNotFoundError(err: unknown): boolean {
  return (
    typeof err === 'object' &&
    err !== null &&
    'data' in err &&
    typeof (err as { data?: { code?: string } }).data?.code === 'string' &&
    (err as { data: { code: string } }).data.code === 'NOT_FOUND'
  );
}

function classifyCreateFailure(err: unknown): CreateTimeEntryFailure {
  if (isPayrollAddonRequiredError(err)) return 'entitlement_required';
  if (isNotFoundError(err)) return 'not_found';
  if (isForbiddenTrpcError(err)) return 'forbidden';
  return 'error';
}

/**
 * Calls the real `payroll.createTimeEntry`. Never throws — every failure
 * resolves a classified {@link CreateTimeEntryResult} instead, mirroring
 * `createEmployeeRecord`'s contract in `use_employee_mutations.ts`.
 */
export async function createTimeEntryRecord(
  entityInput: TimeEntriesFetchInput,
  input: CreateTimeEntryInput,
  deps?: CreateTimeEntryDeps,
): Promise<CreateTimeEntryResult> {
  try {
    const resolvedDeps = deps ?? defaultCreateTimeEntryDeps();
    const result = await resolvedDeps.createTimeEntry({
      ...entityInput,
      ...input,
    });
    return { outcome: 'success', entry_id: result.entry_id };
  } catch (err) {
    console.error('[wagepro-web] createTimeEntry failed:', err);
    return { outcome: 'failure', failure: classifyCreateFailure(err) };
  }
}

// ── Hook ──────────────────────────────────────────────────────────────────

export interface UseTimeEntriesResult {
  state: TimeEntriesState;
  entries: readonly RealTimeEntryRecord[];
  is_loading: boolean;
  retry: () => void;
  /**
   * `undefined` when the write path isn't reachable — no bound entity, the
   * OAuth grant isn't write-capable, or the read hasn't resolved yet.
   * Consumers must hide the affordance entirely when this is `undefined`
   * (never render a button that would only 403).
   */
  create:
    | ((input: CreateTimeEntryInput) => Promise<CreateTimeEntryResult>)
    | undefined;
}

export function useTimeEntries(): UseTimeEntriesResult {
  const entityContext = useOAuthEntityContext();
  const { grant } = useAuth();
  const [records, setRecords] = React.useState<
    RealTimeEntryRecord[] | undefined
  >(undefined);
  const [failure, setFailure] = React.useState<
    TimeEntriesLoadFailure | undefined
  >(undefined);
  const [reloadToken, setReloadToken] = React.useState(0);

  React.useEffect(() => {
    if (entityContext.status !== 'ready') return;
    const firmId: string = entityContext.firm_id;
    const entityId: string = entityContext.entity_id;
    let cancelled = false;
    setRecords(undefined);
    setFailure(undefined);

    async function load(): Promise<void> {
      const result = await fetchTimeEntryRecords(
        { firm_id: firmId, entity_id: entityId },
        undefined,
        () => cancelled,
      );
      if (cancelled) return;
      if (result.outcome === 'success') {
        setRecords(result.records);
      } else {
        setFailure(result.failure);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [entityContext, reloadToken]);

  const state = React.useMemo(
    () => resolveTimeEntriesState({ entityContext, records, failure }),
    [entityContext, records, failure],
  );

  const retry = React.useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  const create = React.useMemo(() => {
    if (entityContext.status !== 'ready') return undefined;
    if (!isWriteCapableGrant(grant)) return undefined;
    const firmId = entityContext.firm_id;
    const entityId = entityContext.entity_id;
    return async (
      input: CreateTimeEntryInput,
    ): Promise<CreateTimeEntryResult> => {
      const result = await createTimeEntryRecord(
        { firm_id: firmId, entity_id: entityId },
        input,
      );
      if (result.outcome === 'success') {
        // Real re-fetch (never optimistic) so the newly-created row lands
        // via the same read path that classifies failures — mirrors
        // `use_data_centre.ts`'s `save_allowance`/`save_deduction`.
        setReloadToken((token) => token + 1);
      }
      return result;
    };
  }, [entityContext, grant]);

  return {
    state,
    entries: state.status === 'ready' ? state.entries : [],
    is_loading: state.status === 'loading',
    retry,
    create,
  };
}
